// Unified contact/patient view — profile + citas + prospectos + conversaciones
// for one person. Composes the locked design system; es-MX copy.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import {
  type ContactDetail,
  type ContactField,
  type ContactSummary,
  getContact,
  getContacts,
  mergeContact,
  updateContact,
} from './contacts-data.js'
import { useSession } from '../../auth/session.js'
import {
  Button,
  Chip,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'
// UNA sola frase para el valor que ya no está en la lista: la ficha del
// prospecto y la del contacto dibujan el mismo caso y tienen que decir lo mismo.
import { HISTORICAL_OPTION_HINT, useHistoricalOptions } from '../leads/lead-fields-form.js'

const fmtDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })

const STATUS_TONE: Record<string, 'ok' | 'info' | 'warn' | 'draft'> = {
  confirmed: 'ok',
  scheduled: 'info',
  completed: 'ok',
  cancelled: 'draft',
  no_show: 'warn',
}
const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Confirmada',
  scheduled: 'Pendiente',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
}
const LIFECYCLE_LABEL: Record<string, string> = {
  lead: 'Prospecto',
  customer: 'Cliente',
  churned: 'Inactivo',
}
const LIFECYCLE_TONE: Record<string, 'ok' | 'info' | 'draft'> = {
  lead: 'info',
  customer: 'ok',
  churned: 'draft',
}
const CONTACT_STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  do_not_contact: 'No contactar',
  opted_out: 'Dado de baja',
  bounced: 'Rebotado',
  merged: 'Fusionado',
}
const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  messenger: 'Messenger',
}

/** Render a stored custom-field value for display (— when unset). */
function formatFieldValue(f: ContactField): string {
  const v = f.value
  if (v === null || v === undefined || v === '') return '—'
  if (f.dataType === 'boolean') return v ? 'Sí' : 'No'
  if (f.dataType === 'date' && typeof v === 'string') return fmtDate(v)
  if (Array.isArray(v)) return v.join(', ')
  if (typeof v === 'number') return String(v)
  return typeof v === 'string' ? v : JSON.stringify(v)
}

export function ContactDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const session = useSession()
  // Read-only viewers see the contact but not the write controls, which the
  // API gates with contacts.write (edit) / tenant.config.write (merge) and
  // 403s. Hide them instead of surfacing an error toast on click. UI hint
  // only; the API still enforces every write.
  const canEdit = session?.canEditContacts ?? false
  const canMerge = session?.canManageWorkspace ?? false
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['contact', id],
    queryFn: () => getContact(id!),
    enabled: Boolean(id),
  })
  const [editing, setEditing] = useState(false)
  const [merging, setMerging] = useState(false)

  if (isError) {
    return (
      <div data-testid="contact-detail">
        <QueryError message="No pudimos cargar este contacto." onRetry={() => void refetch()} />
      </div>
    )
  }
  if (isLoading || !data) {
    return (
      <div data-testid="contact-detail">
        <Spinner label="Cargando contacto…" />
      </div>
    )
  }

  const c = data.contact

  return (
    <div data-testid="contact-detail" className="mx-auto w-full max-w-5xl">
      <PageHeader
        title={c.displayName}
        subtitle={c.phone}
        actions={
          (canMerge || canEdit) && (
            <div className="flex gap-2">
              {canMerge && (
                <Button size="sm" variant="ghost" onClick={() => setMerging(true)}>
                  Fusionar
                </Button>
              )}
              {canEdit && (
                <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                  Editar
                </Button>
              )}
            </div>
          )
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <aside className="space-y-4 rounded-xl border border-line bg-surface p-4 md:col-span-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip tone={LIFECYCLE_TONE[c.lifecycleStage] ?? 'info'} dot>
              {LIFECYCLE_LABEL[c.lifecycleStage] ?? c.lifecycleStage}
            </Chip>
            {c.status !== 'active' && (
              <Chip tone="warn">{CONTACT_STATUS_LABEL[c.status] ?? c.status}</Chip>
            )}
          </div>
          {/* A3: era `fullName ?? profileName ?? '—'`, es decir "—" para una
              persona dada de alta a mano, cuyo nombre está en `display_name` y
              se leía en el encabezado justo arriba. Es el MISMO nombre
              canónico que resuelve el servidor. */}
          <Detail label="Nombre" value={c.displayName} />
          {/* El nombre crudo que Meta manda con el perfil. Solo cuando existe:
              es un dato, no un campo del expediente, y decir "Nombre" dos veces
              hacía imposible saber cuál se estaba editando. */}
          {c.profileName ? <Detail label="Nombre en WhatsApp" value={c.profileName} /> : null}
          <Detail label="Teléfono" value={c.phone || '—'} />
          <Detail label="Correo" value={c.email ?? '—'} />
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text-2">
              Etiquetas
            </p>
            {c.tags.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {c.tags.map((t) => (
                  <Chip key={t} tone="info">
                    {t}
                  </Chip>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-text">—</p>
            )}
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text-2">Notas</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-text">{c.notes ? c.notes : '—'}</p>
          </div>
        </aside>

        <div className="space-y-4 md:col-span-2">
          {data.fields.length > 0 && (
            <Section title="Datos">
              {data.fields.map((f) => (
                <RowItem
                  key={f.key}
                  title={f.label}
                  subtitle=""
                  right={<span className="text-sm text-text">{formatFieldValue(f)}</span>}
                />
              ))}
            </Section>
          )}

          {data.identities.length > 0 && (
            <Section title="Canales">
              {data.identities.map((i) => (
                <RowItem
                  key={`${i.channel}:${i.externalId}`}
                  title={CHANNEL_LABEL[i.channel] ?? i.channel}
                  subtitle={i.externalId}
                  right={
                    i.verified ? (
                      <Chip tone="ok" dot>
                        Verificado
                      </Chip>
                    ) : (
                      <span className="text-xs text-text-2">{fmtDate(i.firstSeen)}</span>
                    )
                  }
                />
              ))}
            </Section>
          )}

          <Section title="Citas">
            {data.appointments.length === 0 ? (
              <Empty>Sin citas registradas.</Empty>
            ) : (
              data.appointments.map((a) => (
                <RowItem
                  key={a.id}
                  title={`${a.doctorName} · ${a.specialty}`}
                  subtitle={fmtDateTime(a.startsAt)}
                  right={
                    <Chip tone={STATUS_TONE[a.status] ?? 'draft'}>
                      {STATUS_LABEL[a.status] ?? a.status}
                    </Chip>
                  }
                />
              ))
            )}
          </Section>

          <Section title="Prospectos">
            {data.leads.length === 0 ? (
              <Empty>Sin prospectos.</Empty>
            ) : (
              // A7 — la vista D7: una persona puede traer una tarjeta en
              // Prospectos y otra en Urgencias, y esta lista jamás decía en QUÉ
              // proceso vivía cada una. La etapa, además, se traducía con un
              // mapa de cinco claves escrito a mano, así que cualquier proceso
              // que el cliente arme imprimía la clave cruda. Ahora los dos
              // nombres los resuelve el servidor con el proceso de la tarjeta.
              data.leads.map((l) => (
                <Link key={l.id} to={`/leads/${l.id}`} className="block">
                  <RowItem
                    title={l.name}
                    subtitle={[l.pipelineName, l.stageName].filter(Boolean).join(' · ')}
                    right={
                      <Chip tone={l.open ? 'info' : 'draft'}>{l.open ? 'Abierto' : 'Cerrado'}</Chip>
                    }
                  />
                </Link>
              ))
            )}
          </Section>

          <Section title="Conversaciones">
            {data.conversations.length === 0 ? (
              <Empty>Sin conversaciones.</Empty>
            ) : (
              data.conversations.map((cv) => (
                <RowItem
                  key={cv.id}
                  title="Conversación"
                  subtitle={`Iniciada ${fmtDate(cv.startedAt)}`}
                  right={
                    <span className="text-xs text-text-2">
                      {cv.lastMessageAt ? fmtDate(cv.lastMessageAt) : ''}
                    </span>
                  }
                />
              ))
            )}
          </Section>
        </div>
      </div>

      {editing && (
        <EditContactModal
          detail={data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            void qc.invalidateQueries({ queryKey: ['contact', id] })
            void qc.invalidateQueries({ queryKey: ['contacts'] })
          }}
        />
      )}

      {merging && (
        <MergeContactModal
          survivor={{ id: c.id, displayName: c.displayName, phone: c.phone }}
          onClose={() => setMerging(false)}
          onMerged={() => {
            setMerging(false)
            void qc.invalidateQueries({ queryKey: ['contact', id] })
            void qc.invalidateQueries({ queryKey: ['contacts'] })
          }}
        />
      )}
    </div>
  )
}

function MergeContactModal({
  survivor,
  onClose,
  onMerged,
}: {
  survivor: { id: string; displayName: string; phone: string }
  onClose: () => void
  onMerged: () => void
}): JSX.Element {
  const [query, setQuery] = useState('')
  const [loser, setLoser] = useState<ContactSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const { data: results, isFetching } = useQuery({
    queryKey: ['contact-merge-search', query],
    queryFn: () => getContacts(query),
    enabled: query.trim().length >= 2,
  })
  const candidates = (results ?? []).filter((c) => c.id !== survivor.id)

  async function submit(): Promise<void> {
    if (!loser) return
    setBusy(true)
    try {
      await mergeContact(survivor.id, loser.id)
      toast.ok('Contactos fusionados')
      onMerged()
    } catch {
      toast.error('No se pudo fusionar', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Fusionar contacto"
      subtitle={`Une un duplicado a ${survivor.displayName}. Este contacto se conserva.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            className="ml-auto"
            disabled={busy || !loser}
            onClick={() => void submit()}
          >
            {busy ? 'Fusionando…' : 'Fusionar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {loser ? (
          <div className="rounded-lg border border-line bg-surface-2 p-3 text-sm">
            <p className="text-text">
              Se unirá <span className="font-semibold">{loser.displayName}</span> ({loser.phone}) a
              este contacto.
            </p>
            <p className="mt-1 text-text-3">
              Su historial (conversaciones, citas, prospectos y datos) pasa aquí. Esta acción no se
              puede deshacer.
            </p>
            <Button size="sm" variant="ghost" className="mt-2" onClick={() => setLoser(null)}>
              Elegir otro
            </Button>
          </div>
        ) : (
          <>
            <FormField label="Busca el contacto duplicado" htmlFor="merge-search">
              <Input
                id="merge-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Nombre o teléfono"
                autoFocus
              />
            </FormField>
            <div className="max-h-64 overflow-y-auto">
              {isFetching ? (
                <Spinner label="Buscando…" />
              ) : query.trim().length < 2 ? (
                <p className="px-2 py-1 text-sm text-text-3">Escribe al menos 2 letras.</p>
              ) : candidates.length === 0 ? (
                <p className="px-2 py-1 text-sm text-text-3">Sin resultados.</p>
              ) : (
                candidates.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setLoser(c)}
                    className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left hover:bg-surface-2"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-text">
                        {c.displayName}
                      </span>
                      <span className="block truncate text-xs text-text-3">{c.phone}</span>
                    </span>
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

function Detail({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-2">{label}</p>
      <p className="mt-0.5 text-sm text-text">{value}</p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <section className="rounded-xl border border-line bg-surface p-4">
      <h2 className="mb-2 text-sm font-semibold text-text">{title}</h2>
      <div className="space-y-1">{children}</div>
    </section>
  )
}

function RowItem({
  title,
  subtitle,
  right,
}: {
  title: string
  subtitle: string
  right: ReactNode
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-surface-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text">{title}</p>
        <p className="truncate text-xs text-text-2">{subtitle}</p>
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  )
}

function Empty({ children }: { children: ReactNode }): JSX.Element {
  return <p className="px-2 py-1 text-sm text-text-2">{children}</p>
}

/** String-encode a stored field value for the edit form. */
function encodeFieldValue(f: ContactField): string {
  const v = f.value
  if (v === null || v === undefined) return ''
  if (typeof v === 'boolean') return v ? 'true' : 'false'
  if (Array.isArray(v)) return v.join(', ')
  if (typeof v === 'number') return String(v)
  return typeof v === 'string' ? v : JSON.stringify(v)
}

/** Decode a form string back to the typed value the API expects. */
function decodeFieldValue(f: ContactField, raw: string): string | number | boolean {
  if (f.dataType === 'number') return Number(raw)
  if (f.dataType === 'boolean') return raw === 'true'
  return raw
}

const LIFECYCLE_OPTIONS = [
  { value: 'lead', label: 'Prospecto' },
  { value: 'customer', label: 'Cliente' },
  { value: 'churned', label: 'Inactivo' },
]
const STATUS_OPTIONS = [
  { value: 'active', label: 'Activo' },
  { value: 'do_not_contact', label: 'No contactar' },
]

function EditContactModal({
  detail,
  onClose,
  onSaved,
}: {
  detail: ContactDetail
  onClose: () => void
  onSaved: () => void
}): JSX.Element {
  const c = detail.contact
  // A3: el campo edita el nombre CANÓNICO. Se sembraba y se guardaba en
  // `fullName`, al que `display_name` le gana en la cadena de respaldo, así que
  // renombrar a una persona dada de alta a mano no cambiaba nada en pantalla.
  const [displayName, setDisplayName] = useState(c.displayName)
  const [email, setEmail] = useState(c.email ?? '')
  const [notes, setNotes] = useState(c.notes ?? '')
  const [tagsText, setTagsText] = useState(c.tags.join(', '))
  const [lifecycle, setLifecycle] = useState(c.lifecycleStage)
  const [status, setStatus] = useState(c.status)
  const initialFields = useMemo(
    () => Object.fromEntries(detail.fields.map((f) => [f.key, encodeFieldValue(f)])),
    [detail.fields],
  )
  const [fieldVals, setFieldVals] = useState<Record<string, string>>(initialFields)
  // El mismo recuerdo que la ficha del prospecto: un valor de lista que ya no
  // está en la lista sigue elegible mientras el modal está abierto (D21-2).
  const historicalFor = useHistoricalOptions()
  const [busy, setBusy] = useState(false)
  // opted_out / bounced / merged are system-driven; only offer the toggle when
  // the person is in an editable status.
  const statusEditable = c.status === 'active' || c.status === 'do_not_contact'

  async function submit(): Promise<void> {
    setBusy(true)
    try {
      const patch: Parameters<typeof updateContact>[1] = {
        displayName: displayName.trim() ? displayName.trim() : null,
        email: email.trim() ? email.trim() : null,
        notes: notes.trim() ? notes.trim() : null,
        tags: tagsText
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      }
      if (lifecycle !== c.lifecycleStage) patch.lifecycleStage = lifecycle
      if (statusEditable && status !== c.status)
        patch.status = status as 'active' | 'do_not_contact'
      const fields: Record<string, string | number | boolean | null> = {}
      for (const f of detail.fields) {
        const cur = fieldVals[f.key] ?? ''
        if (cur === (initialFields[f.key] ?? '')) continue // unchanged
        fields[f.key] = cur.trim() === '' ? null : decodeFieldValue(f, cur)
      }
      if (Object.keys(fields).length > 0) patch.fields = fields
      await updateContact(c.id, patch)
      toast.ok('Contacto actualizado')
      onSaved()
    } catch {
      toast.error('No se pudo guardar', { description: 'Inténtalo de nuevo.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Editar contacto"
      subtitle="Actualiza el perfil de esta persona."
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button className="ml-auto" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Nombre" htmlFor="ct-name">
          <Input
            id="ct-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Etapa" htmlFor="ct-lifecycle">
            <Select
              id="ct-lifecycle"
              options={LIFECYCLE_OPTIONS}
              value={lifecycle}
              onChange={(e) => setLifecycle(e.target.value as typeof lifecycle)}
            />
          </FormField>
          {statusEditable && (
            <FormField label="Contacto" htmlFor="ct-status">
              <Select
                id="ct-status"
                options={STATUS_OPTIONS}
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
              />
            </FormField>
          )}
        </div>
        <FormField label="Correo" htmlFor="ct-email" required="optional">
          <Input
            id="ct-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormField>
        <FormField
          label="Etiquetas"
          htmlFor="ct-tags"
          required="optional"
          helper="Separadas por comas."
        >
          <Input
            id="ct-tags"
            value={tagsText}
            onChange={(e) => setTagsText(e.target.value)}
            placeholder="vip, cardiología"
          />
        </FormField>
        <FormField label="Notas" htmlFor="ct-notes" required="optional">
          <Textarea id="ct-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>

        {detail.fields.map((f) => {
          const id = `ct-field-${f.key}`
          const val = fieldVals[f.key] ?? ''
          const set = (v: string): void => setFieldVals((prev) => ({ ...prev, [f.key]: v }))
          // El mismo valor HISTÓRICO que la ficha del prospecto (D21-2): lo que
          // el contacto ya trae y la lista viva no ofrece sigue elegible, en vez
          // de dibujarse vacío y borrarse en el primer guardado.
          const historical =
            f.dataType === 'enum' ? historicalFor(f.key, val, f.options ?? []) : null
          return (
            <FormField
              key={f.key}
              label={f.label}
              htmlFor={id}
              required="optional"
              {...(historical !== null && historical === val
                ? { helper: HISTORICAL_OPTION_HINT }
                : {})}
            >
              {f.dataType === 'enum' ? (
                <Select
                  id={id}
                  options={[
                    { value: '', label: '—' },
                    ...(historical !== null ? [{ value: historical, label: historical }] : []),
                    ...(f.options ?? []).map((o) => ({ value: o, label: o })),
                  ]}
                  value={val}
                  onChange={(e) => set(e.target.value)}
                />
              ) : f.dataType === 'boolean' ? (
                <Select
                  id={id}
                  options={[
                    { value: '', label: '—' },
                    { value: 'true', label: 'Sí' },
                    { value: 'false', label: 'No' },
                  ]}
                  value={val}
                  onChange={(e) => set(e.target.value)}
                />
              ) : (
                <Input
                  id={id}
                  type={
                    f.dataType === 'number' ? 'number' : f.dataType === 'date' ? 'date' : 'text'
                  }
                  value={val}
                  onChange={(e) => set(e.target.value)}
                />
              )}
            </FormField>
          )
        })}
      </div>
    </Modal>
  )
}
